import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { Send, Hash, Users, UserPlus, Check, X, MessageCircle, Image, X as XIcon, Loader2 } from 'lucide-react';

export default function Messenger({ navigate }) {
  const { user, profile } = useAuth();
  const [view, setView] = useState('channels'); // channels, dms, friends
  const [channels] = useState([
    { id: 'general', name: 'general', description: 'General discussion' },
    { id: 'random', name: 'random', description: 'Off-topic chat' },
    { id: 'announcements', name: 'announcements', description: 'Important updates' }
  ]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedDM, setSelectedDM] = useState(null);
  const [messages, setMessages] = useState([]);
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const messagesEndRef = useRef(null);
  const channelRef = useRef(null);
  const imageInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    fetchFriends();
    fetchFriendRequests();
    fetchAllUsers();
  }, []);

  useEffect(() => {
    if (selectedChannel) {
      fetchChannelMessages(selectedChannel.id);
      subscribeToChannel(selectedChannel.id);
    }
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [selectedChannel]);

  useEffect(() => {
    if (selectedDM) {
      fetchDMMessages(selectedDM.id);
      subscribeToDM(selectedDM.id);
    }
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [selectedDM]);

  const ADMIN_USER = {
    id: 'admin-sage',
    username: 'Ryan March',
    email: 'rhinomarch78@gmail.com'
  };

  const fetchFriends = async () => {
    try {
      const { data } = await supabase
        .from('friendships')
        .select('*, friend:friend_id(id, username, email)')
        .eq('user_id', user.id)
        .eq('status', 'accepted');
      
      const friendsList = data?.map(f => f.friend).filter(Boolean) || [];
      
      // Also get reverse friendships
      const { data: data2 } = await supabase
        .from('friendships')
        .select('*, requester:user_id(id, username, email)')
        .eq('friend_id', user.id)
        .eq('status', 'accepted');
      
      const friendsList2 = data2?.map(f => f.requester).filter(Boolean) || [];
      
      // Combine all friends
      let allFriends = [...friendsList, ...friendsList2];
      
      // Add admin (Ryan March) as default friend for everyone if not already in list
      if (user.email !== ADMIN_USER.email) {
        const hasAdmin = allFriends.some(f => f.email === ADMIN_USER.email || f.username === ADMIN_USER.username);
        if (!hasAdmin) {
          // Try to fetch admin from users table
          const { data: adminData } = await supabase
            .from('users')
            .select('id, username, email')
            .eq('email', ADMIN_USER.email)
            .single();
          
          if (adminData) {
            allFriends.push(adminData);
          } else {
            // Use default admin object if not in database
            allFriends.push(ADMIN_USER);
          }
        }
      }
      
      setFriends(allFriends);
    } catch (error) {
      console.error('Error fetching friends:', error);
      // Still add admin as friend even on error
      if (user.email !== ADMIN_USER.email) {
        setFriends([ADMIN_USER]);
      } else {
        setFriends([]);
      }
    }
  };

  const fetchFriendRequests = async () => {
    try {
      const { data } = await supabase
        .from('friendships')
        .select('*, requester:user_id(id, username, email)')
        .eq('friend_id', user.id)
        .eq('status', 'pending');
      
      setFriendRequests(data || []);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setFriendRequests([]);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const { data } = await supabase
        .from('users')
        .select('id, username, email')
        .neq('id', user.id);
      setAllUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setAllUsers([]);
    }
  };

  const fetchChannelMessages = async (channelId) => {
    setLoading(true);
    try {
      // Use 'channel' field as per the schema
      const { data } = await supabase
        .from('messages')
        .select('*')
        .eq('channel', channelId)
        .order('created_at', { ascending: true })
        .limit(100);
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToChannel = (channelId) => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }
    
    channelRef.current = supabase
      .channel(`channel-${channelId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `channel=eq.${channelId}`
        },
        (payload) => {
          setMessages(prev => {
            if (prev.find(m => m.id === payload.new.id)) return prev;
            return [...prev, payload.new];
          });
        }
      )
      .subscribe();
  };

  const fetchDMMessages = async (friendId) => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('messages')
        .select('*')
        .or(`and(sender_id.eq.${user.id},recipient_id.eq.${friendId}),and(sender_id.eq.${friendId},recipient_id.eq.${user.id})`)
        .is('channel', null)
        .order('created_at', { ascending: true });
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching DMs:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToDM = (friendId) => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }
    
    channelRef.current = supabase
      .channel(`dm-${user.id}-${friendId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload) => {
          const msg = payload.new;
          if (
            (msg.sender_id === user.id && msg.recipient_id === friendId) ||
            (msg.sender_id === friendId && msg.recipient_id === user.id)
          ) {
            setMessages(prev => {
              if (prev.find(m => m.id === msg.id)) return prev;
              return [...prev, msg];
            });
          }
        }
      )
      .subscribe();
  };

  const sendMessage = async () => {
    if (!input.trim() && !selectedImage) return;
    
    setUploadingImage(!!selectedImage);
    
    try {
      // Build message data - only include image_url if we have an image
      const messageData = {
        sender_id: user.id,
        sender_username: profile?.username || 'User',
        content: selectedImage ? (input.trim() || '[Image]') : input.trim(),
        channel: selectedChannel?.id || null,
        recipient_id: selectedDM?.id || null
      };
      
      // Try to include image_url if image is selected
      if (selectedImage) {
        messageData.image_url = selectedImage;
      }
      
      const { data, error } = await supabase.from('messages').insert(messageData).select();
      
      if (error) {
        // If error is about image_url column not existing, retry without it
        if (error.message?.includes('image_url')) {
          console.log('image_url column not found, sending without image');
          const { content, ...msgWithoutImage } = messageData;
          const contentWithImage = selectedImage 
            ? `${input.trim()}\n[Image attached - please add image_url column to messages table]`
            : input.trim();
          
          const { error: retryError } = await supabase.from('messages').insert({
            ...msgWithoutImage,
            content: contentWithImage
          });
          
          if (retryError) throw retryError;
        } else {
          throw error;
        }
      }
      
      setInput('');
      clearSelectedImage();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message: ' + error.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const sendFriendRequest = async (friendId) => {
    try {
      await supabase.from('friendships').insert({
        user_id: user.id,
        friend_id: friendId,
        status: 'pending'
      });
      fetchAllUsers();
    } catch (error) {
      console.error('Error sending friend request:', error);
    }
  };

  const acceptFriendRequest = async (requestId) => {
    try {
      await supabase
        .from('friendships')
        .update({ status: 'accepted' })
        .eq('id', requestId);
      fetchFriends();
      fetchFriendRequests();
    } catch (error) {
      console.error('Error accepting request:', error);
    }
  };

  const rejectFriendRequest = async (requestId) => {
    try {
      await supabase.from('friendships').delete().eq('id', requestId);
      fetchFriendRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const clearSelectedImage = () => {
    setSelectedImage(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  // Check if user is already a friend or has pending request
  const isFriend = (userId) => friends.some(f => f.id === userId);

  return (
    <div className="flex flex-col h-screen bg-[#08080c]" data-testid="messenger-screen">
      {/* Navigation Tabs */}
      <div className="flex border-b border-[#1a1a24]">
        {[
          { id: 'channels', label: 'Channels', icon: Hash },
          { id: 'dms', label: 'Direct', icon: MessageCircle },
          { id: 'friends', label: 'Friends', icon: Users }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setView(tab.id);
              setSelectedChannel(null);
              setSelectedDM(null);
              setMessages([]);
            }}
            className={`flex-1 py-3 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-wider transition-all ${
              view === tab.id 
                ? 'text-[#00d4ff] border-b border-[#00d4ff] bg-[#00d4ff]/5' 
                : 'text-[#4a4a60] hover:text-[#7a7a9a]'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Channels View */}
        {view === 'channels' && !selectedChannel && (
          <div className="p-4 space-y-2 overflow-y-auto" data-testid="channels-list">
            <p className="font-mono text-[10px] text-[#4a4a60] uppercase tracking-wider mb-3">Channels</p>
            {channels.map(channel => (
              <button
                key={channel.id}
                onClick={() => setSelectedChannel(channel)}
                className="w-full bg-[#0d0d12] border border-[#1a1a24] p-3 flex items-center gap-3 hover:border-[#00d4ff]/30 transition-all text-left"
                data-testid={`channel-${channel.id}`}
              >
                <Hash className="w-4 h-4 text-[#00d4ff]/60" />
                <div>
                  <p className="text-xs text-[#00d4ff]">{channel.name}</p>
                  <p className="text-[10px] text-[#4a4a60]">{channel.description}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* DMs View */}
        {view === 'dms' && !selectedDM && (
          <div className="p-4 space-y-2 overflow-y-auto" data-testid="dms-list">
            <p className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-wider mb-3">Direct Messages</p>
            {friends.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[#4a4a60] text-xs font-mono">No friends yet</p>
                <p className="text-[#4a4a60] text-[10px] mt-1">Add friends to start messaging</p>
              </div>
            ) : (
              friends.map(friend => {
                const friendAvatar = localStorage.getItem(`sage_avatar_${friend.id}`);
                return (
                  <button
                    key={friend.id}
                    onClick={() => setSelectedDM(friend)}
                    className="w-full bg-[#0d0d12] border border-[#00d4ff]/30 p-3 flex items-center gap-3 hover:border-[#00d4ff] transition-all text-left rounded-lg"
                    data-testid={`dm-${friend.id}`}
                  >
                    {friendAvatar ? (
                      <img 
                        src={friendAvatar}
                        alt={friend.username}
                        className="w-10 h-10 rounded-lg object-cover border border-[#00d4ff]/30"
                      />
                    ) : (
                      <div className="w-10 h-10 border border-[#00d4ff]/30 rounded-lg flex items-center justify-center bg-[#00d4ff]/5">
                        <span className="font-mono text-sm text-[#00d4ff]">
                          {friend.username?.[0]?.toUpperCase() || '?'}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-[#00d4ff]">{friend.username}</p>
                      <p className="text-[10px] text-[#4a4a60]">{friend.email}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}

        {/* Friends View */}
        {view === 'friends' && (
          <div className="p-4 overflow-y-auto" data-testid="friends-view">
            {/* Friend Requests */}
            {friendRequests.length > 0 && (
              <div className="mb-6">
                <p className="font-mono text-[10px] text-[#ffd700] uppercase tracking-wider mb-3">
                  Friend Requests ({friendRequests.length})
                </p>
                <div className="space-y-2">
                  {friendRequests.map(req => {
                    const reqAvatar = localStorage.getItem(`sage_avatar_${req.requester?.id}`);
                    return (
                      <div key={req.id} className="bg-[#0d0d12] border border-[#ffd700]/30 p-3 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {reqAvatar ? (
                            <img 
                              src={reqAvatar}
                              alt={req.requester?.username}
                              className="w-10 h-10 rounded-lg object-cover border border-[#ffd700]/30"
                            />
                          ) : (
                            <div className="w-10 h-10 border border-[#ffd700]/30 rounded-lg flex items-center justify-center bg-[#ffd700]/5">
                              <span className="font-mono text-sm text-[#ffd700]">
                                {req.requester?.username?.[0]?.toUpperCase() || '?'}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="text-xs text-[#ffd700]">{req.requester?.username}</p>
                            <p className="text-[10px] text-[#4a4a60]">{req.requester?.email}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => acceptFriendRequest(req.id)}
                            className="px-3 py-2 bg-[#00ff88] text-[#000] font-mono text-[9px] uppercase font-bold rounded hover:bg-[#00ff88]/90 transition-all"
                            style={{ boxShadow: '0 0 10px rgba(0, 255, 136, 0.3)' }}
                            data-testid={`accept-${req.id}`}
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => rejectFriendRequest(req.id)}
                            className="px-3 py-2 border border-[#ff3366]/50 text-[#ff3366] font-mono text-[9px] uppercase rounded hover:bg-[#ff3366]/10 transition-all"
                            data-testid={`reject-${req.id}`}
                          >
                            Decline
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Current Friends */}
            <div className="mb-6">
              <p className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-wider mb-3">
                Your Friends ({friends.length})
              </p>
              {friends.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-[#4a4a60] text-xs font-mono">No friends yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {friends.map(friend => {
                    const friendAvatar = localStorage.getItem(`sage_avatar_${friend.id}`);
                    return (
                      <div key={friend.id} className="bg-[#0d0d12] border border-[#00d4ff]/30 p-3 rounded-lg flex items-center gap-3">
                        {friendAvatar ? (
                          <img 
                            src={friendAvatar}
                            alt={friend.username}
                            className="w-10 h-10 rounded-lg object-cover border border-[#00d4ff]/30"
                          />
                        ) : (
                          <div className="w-10 h-10 border border-[#00d4ff]/30 rounded-lg flex items-center justify-center bg-[#00d4ff]/5">
                            <span className="font-mono text-sm text-[#00d4ff]">
                              {friend.username?.[0]?.toUpperCase() || '?'}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-[#00d4ff]">{friend.username}</p>
                          <p className="text-[10px] text-[#4a4a60]">{friend.email}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Add Friends */}
            <div>
              <p className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-wider mb-3">Add Friends</p>
              <div className="space-y-2">
                {allUsers.filter(u => !isFriend(u.id)).length === 0 ? (
                  <p className="text-[#4a4a60] text-xs font-mono text-center py-4">No users to add</p>
                ) : (
                  allUsers.filter(u => !isFriend(u.id)).map(u => {
                    const userAvatar = localStorage.getItem(`sage_avatar_${u.id}`);
                    return (
                      <div key={u.id} className="bg-[#0d0d12] border border-[#1a1a24] p-3 rounded-lg flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {userAvatar ? (
                            <img 
                              src={userAvatar}
                              alt={u.username}
                              className="w-10 h-10 rounded-lg object-cover border border-[#1a1a24]"
                            />
                          ) : (
                            <div className="w-10 h-10 border border-[#1a1a24] rounded-lg flex items-center justify-center bg-[#0d0d12]">
                              <span className="font-mono text-sm text-[#4a4a60]">
                                {u.username?.[0]?.toUpperCase() || '?'}
                              </span>
                            </div>
                          )}
                          <div>
                            <p className="text-xs text-[#e8e8f0]">{u.username}</p>
                            <p className="text-[10px] text-[#4a4a60]">{u.email}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => sendFriendRequest(u.id)}
                          className="px-3 py-2 bg-[#00d4ff] text-[#000] font-mono text-[9px] uppercase font-bold rounded hover:bg-[#00d4ff]/90 transition-all flex items-center gap-1"
                          style={{ boxShadow: '0 0 10px rgba(0, 212, 255, 0.3)' }}
                          data-testid={`add-friend-${u.id}`}
                        >
                          <UserPlus className="w-3 h-3" />
                          Add
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

        {/* Chat View (Channel or DM selected) */}
        {(selectedChannel || selectedDM) && (
          <>
            {/* Chat Header */}
            <div className="p-3 border-b border-[#1a1a24] flex items-center gap-3">
              <button
                onClick={() => {
                  setSelectedChannel(null);
                  setSelectedDM(null);
                  setMessages([]);
                }}
                className="text-[#4a4a60] hover:text-[#00d4ff] text-xs font-mono"
              >
                ← Back
              </button>
              <div className="flex items-center gap-2">
                {selectedChannel ? (
                  <>
                    <Hash className="w-4 h-4 text-[#00d4ff]" />
                    <span className="text-xs text-[#e8e8f0]">{selectedChannel.name}</span>
                  </>
                ) : (
                  <>
                    <div className="w-6 h-6 border border-[#00d4ff]/30 flex items-center justify-center bg-[#00d4ff]/5">
                      <span className="font-mono text-[10px] text-[#00d4ff]">
                        {selectedDM?.username?.[0]?.toUpperCase()}
                      </span>
                    </div>
                    <span className="text-xs text-[#e8e8f0]">{selectedDM?.username}</span>
                  </>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-20">
              {loading ? (
                <div className="text-center py-8">
                  <p className="text-[#00d4ff] text-xs font-mono animate-pulse">Loading...</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-[#4a4a60] text-xs font-mono">No messages yet</p>
                </div>
              ) : (
                messages.map(msg => {
                  const isOwnMessage = msg.sender_id === user.id;
                  const senderAvatar = localStorage.getItem(`sage_avatar_${msg.sender_id}`);
                  
                  return (
                    <div
                      key={msg.id}
                      className={`flex gap-2 ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}
                    >
                      {/* Profile Picture */}
                      {senderAvatar ? (
                        <img 
                          src={senderAvatar}
                          alt={msg.sender_username}
                          className="w-8 h-8 rounded-lg object-cover border border-[#00d4ff]/30 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 border border-[#00d4ff]/30 rounded-lg flex items-center justify-center bg-[#00d4ff]/5 flex-shrink-0">
                          <span className="font-mono text-[10px] text-[#00d4ff]">
                            {msg.sender_username?.[0]?.toUpperCase() || '?'}
                          </span>
                        </div>
                      )}
                      
                      <div className={`max-w-[70%] ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                        {/* Username on top */}
                        <p className={`font-mono text-[9px] text-[#00d4ff] mb-1 ${isOwnMessage ? 'text-right' : 'text-left'}`}>
                          {msg.sender_username || 'User'}
                        </p>
                        
                        <div className={`p-3 rounded-lg ${
                          isOwnMessage
                            ? 'bg-[#00d4ff]/10 border border-[#00d4ff]/20'
                            : 'bg-[#0d0d12] border border-[#1a1a24]'
                        }`}>
                          {msg.image_url && (
                            <img 
                              src={msg.image_url} 
                              alt="Shared" 
                              className="max-w-full rounded mb-2 cursor-pointer hover:opacity-90 transition-opacity"
                              style={{ maxHeight: '200px' }}
                              onClick={() => window.open(msg.image_url, '_blank')}
                            />
                          )}
                          {msg.content && msg.content !== '[Image]' && (
                            <p className="text-xs text-[#e8e8f0]">{msg.content}</p>
                          )}
                          <p className={`text-[9px] text-[#4a4a60] mt-1 ${isOwnMessage ? 'text-right' : 'text-left'}`}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="absolute bottom-14 left-0 right-0 p-4 bg-[#08080c] border-t border-[#1a1a24]">
              {/* Image Preview */}
              {selectedImage && (
                <div className="max-w-md mx-auto mb-3 relative inline-block">
                  <img 
                    src={selectedImage} 
                    alt="Preview" 
                    className="max-h-32 rounded border border-[#00d4ff]/30"
                  />
                  <button
                    onClick={clearSelectedImage}
                    className="absolute -top-2 -right-2 bg-[#ff3366] rounded-full p-1 hover:bg-[#ff3366]/80"
                    data-testid="clear-image-btn"
                  >
                    <XIcon className="w-3 h-3 text-white" />
                  </button>
                </div>
              )}
              
              <div className="max-w-md mx-auto flex gap-2">
                {/* Hidden file input */}
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                
                {/* Image upload button */}
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className="border border-[#00d4ff]/50 text-[#00d4ff] p-3 hover:border-[#00d4ff] hover:bg-[#00d4ff]/10 transition-colors"
                  data-testid="image-upload-btn"
                >
                  <Image className="w-4 h-4" />
                </button>
                
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={selectedChannel ? `Message #${selectedChannel.name}` : `Message ${selectedDM?.username}`}
                  className="flex-1 bg-[#0d0d12] border border-[#00d4ff]/50 text-[#e8e8f0] px-4 py-3 text-xs font-mono focus:border-[#00d4ff] outline-none"
                  data-testid="messenger-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={(!input.trim() && !selectedImage) || uploadingImage}
                  className="bg-[#00d4ff] text-[#000] p-3 hover:bg-[#00e5ff] disabled:bg-[#00d4ff]/40 disabled:text-[#000]/50 flex items-center justify-center min-w-[48px] transition-colors"
                  style={{ boxShadow: '0 0 15px rgba(0, 212, 255, 0.3)' }}
                  data-testid="send-btn"
                >
                  {uploadingImage ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
